import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, QueryKey, queryCache } from "react-query";
import { AxiosError } from "axios";

import { ReturnIcon } from "fe-shared/src/components/Icons";
import {
  CollapseComponent,
  LinkButton,
  RecordsSidebar,
  showNotification,
} from "fe-shared/src/components";
import useDialog from "fe-shared/src/hooks/useDialog";
import { ApiError } from "fe-shared/src/types/common";
import { DialogHeader } from "fe-shared-transactions/src/components";
import { Transfer } from "fe-shared-transactions/src/types";

import {
  ActionType,
  InventoryAdjustmentDetails,
} from "../../../types/inventoryAdjustments";
import {
  INVENTORY_ADJUSTMENTS_ACTIVITIES_QUERY_KEY,
  INVENTORY_ADJUSTMENTS_NOTES_QUERY_KEY,
} from "../../../consts";
import EditInventoryAdjustmentDialog, {
  EditInventoryAdjustmentFormContent,
} from "../../../components/EditInventoryAdjustmentDialog";

import InventoryAdjustmentStatus from "./InventoryAdjustmentStatus";
import InventoryAdjustmentDetailsLocationSection from "./InventoryAdjustmentDetailsLocationSection";
import InventoryAdjustmentDetailsGeneralInfoSection from "./InventoryAdjustmentDetailsGeneralInfoSection";
import InventoryAdjustmentDetailsStorageInfoSection from "./InventoryAdjustmentDetailsStorageInfoSection";
import InventoryAdjustmentDetailsActionTransaction from "./InventoryAdjustmentDetailsActionTransaction";
import InventoryAdjustmentActions from "./InventoryAdjustmentActions";

import * as api from "../modules/api";
import { EditableStatusCode } from "../modules/config";
import { EditInventoryAdjustmentParams } from "../modules/types";
import { mapEditParams } from "../modules/helpers";
import styles from "../InventoryAdjustmentsDetails.module.scss";

interface Props {
  returnUrl: string;
  inventoryAdjustment: InventoryAdjustmentDetails;
  isCurrentUserAssignee: boolean;
  canAssign?: boolean;
  hasSidebar?: boolean;
  userEmail?: string;
  canAddNote?: boolean;
  canExportNotes?: boolean;
  canExportHistory?: boolean;
  canEdit?: boolean;
  canInvalidate?: boolean;
  canApproveFirstLevel?: boolean;
  canApproveSecondLevel?: boolean;
  inventoryAdjustmentQueryKey: QueryKey;
}

const InventoryAdjustmentsDetailsView = ({
  inventoryAdjustment,
  returnUrl,
  userEmail,
  isCurrentUserAssignee,
  inventoryAdjustmentQueryKey,
  hasSidebar = false,
  canAssign = false,
  canEdit = false,
  canInvalidate = false,
  canApproveFirstLevel = false,
  canApproveSecondLevel = false,
  canExportHistory = false,
  canAddNote = false,
  canExportNotes = false,
}: Props) => {
  const { t } = useTranslation();
  const {
    addition,
    subtraction,
    statusCode,
    createdBy,
    warehouse,
    scaleTicket,
    attachments,
    ownerCompany,
    completedDate,
    adjustmentDate,
    overallAdjustmentId,
    overallAdjustmentNumber,
  } = inventoryAdjustment;
  const [activeEditAction, setActiveEditAction] = useState<Transfer>(addition);
  const [activeEditActionType, setActiveEditActionType] = useState<ActionType>(
    "addition"
  );

  const activitiesQueryKey: QueryKey = [
    INVENTORY_ADJUSTMENTS_ACTIVITIES_QUERY_KEY,
    overallAdjustmentNumber,
  ];
  const notesQueryKey: QueryKey = [
    INVENTORY_ADJUSTMENTS_NOTES_QUERY_KEY,
    overallAdjustmentNumber,
  ];
  const isEditable =
    canEdit && isCurrentUserAssignee && EditableStatusCode.includes(statusCode);

  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();

  const onEdit = (action: Transfer, actionType: ActionType) => {
    setActiveEditAction(action);
    setActiveEditActionType(actionType);
    openDialog();
  };

  const [editInventoryAdjustment, editInventoryAdjustmentResult] = useMutation<
    unknown,
    AxiosError<ApiError>,
    EditInventoryAdjustmentParams
  >(api.editInventoryAdjustment, {
    throwOnError: true,
    onSuccess: () => {
      queryCache.invalidateQueries(inventoryAdjustmentQueryKey);
      queryCache.invalidateQueries(activitiesQueryKey);
      queryCache.invalidateQueries(notesQueryKey);
      showNotification(t("inventoryAdjustment.details.edit.successToast"));
      closeDialog();
    },
  });
  const { isLoading } = editInventoryAdjustmentResult;

  const onEditSubmit = (values: EditInventoryAdjustmentFormContent) => {
    editInventoryAdjustment(
      mapEditParams({
        inputValues: values,
        actionType: activeEditActionType,
        addition: addition?.materials,
        subtraction: subtraction?.materials,
        overallAdjustmentNumber,
      })
    );
  };

  return (
    <>
      <main className={styles.content}>
        <div className={styles.contentWrapper}>
          <div className={styles.navigation}>
            <LinkButton
              to={returnUrl}
              variant="inline"
              id="request-back-link"
              iconComponent={ReturnIcon}
              className={styles.reqDetailsBackLink}
            >
              {t("inventoryAdjustment.grid.title")}
            </LinkButton>
          </div>
          <div className={styles.data}>
            <InventoryAdjustmentStatus
              inventoryAdjustment={inventoryAdjustment}
              canAssign={canAssign}
            />
            <InventoryAdjustmentDetailsLocationSection
              completedDate={completedDate}
              createdBy={createdBy}
              ownerCompany={ownerCompany}
            />
            {warehouse.location?.address && (
              <InventoryAdjustmentDetailsStorageInfoSection
                address={warehouse.location.address}
              />
            )}
            <InventoryAdjustmentDetailsGeneralInfoSection
              attachments={attachments}
              scaleTicket={scaleTicket}
              adjustmentDate={adjustmentDate}
            />
            {addition && (
              <InventoryAdjustmentDetailsActionTransaction
                type="addition"
                action={inventoryAdjustment.addition}
                isEditable={isEditable}
                onEdit={() => onEdit(inventoryAdjustment.addition, "addition")}
              />
            )}
            {subtraction && (
              <InventoryAdjustmentDetailsActionTransaction
                type="subtraction"
                action={inventoryAdjustment.subtraction}
                isEditable={isEditable}
                onEdit={() => {
                  onEdit(inventoryAdjustment.subtraction, "subtraction");
                }}
              />
            )}
            <InventoryAdjustmentActions
              isCurrentUserAssignee={isCurrentUserAssignee}
              inventoryAdjustment={inventoryAdjustment}
              canInvalidate={canInvalidate}
              canApproveFirstLevel={canApproveFirstLevel}
              canApproveSecondLevel={canApproveSecondLevel}
              activitiesQueryKey={activitiesQueryKey}
              inventoryAdjustmentQueryKey={inventoryAdjustmentQueryKey}
            />
          </div>
        </div>
      </main>
      {hasSidebar && (
        <CollapseComponent storageKey="sidebarCollapsed">
          <RecordsSidebar
            objectId={overallAdjustmentNumber}
            entityType="overallAdjustment"
            initiatorEmail={userEmail || ""}
            getDialogTitle={(title: string) => (
              <DialogHeader
                type="InventoryAdjustment"
                title={title}
                number={overallAdjustmentNumber}
                translationRoot="inventoryAdjustment"
              />
            )}
            canViewHistory
            canViewNotes
            canExportHistory={canExportHistory}
            canAddNote={canAddNote}
            canExportNotes={canExportNotes}
            fileNamePrefix={t(
              "inventoryAdjustment.details.export.fileNamePrefix",
              { entityNumber: overallAdjustmentNumber }
            )}
            fileNameSuffix=""
            entityName={t("inventoryAdjustment.details.entityName")}
            activitiesQueryKey={activitiesQueryKey}
            notesQueryKey={notesQueryKey}
            getNotes={() => {
              return api.getInventoryAdjustmentNotes(overallAdjustmentId);
            }}
            addNote={(text: string) => {
              return api.addInventoryAdjustmentNote({
                overallAdjustmentId,
                text,
              });
            }}
          />
        </CollapseComponent>
      )}

      {isDialogRendered && (
        <EditInventoryAdjustmentDialog
          isLoading={isLoading}
          onClose={closeDialog}
          onSubmit={onEditSubmit}
          action={activeEditAction}
          type={activeEditActionType}
          ownerCompany={ownerCompany}
          isDialogActive={isDialogActive}
          address={warehouse.location?.address}
          warehouseId={warehouse.warehouseId}
        />
      )}
    </>
  );
};

export default InventoryAdjustmentsDetailsView;
